import React from "react";
import { Box, Typography, useTheme, keyframes } from "@mui/material";
import { DirectionsBus } from "@mui/icons-material";

interface LoadingProps {
  message?: string;
  fullScreen?: boolean;
}

// Minimal keyframe animations
const pulse = keyframes`
  0% {
    opacity: 0.7;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.7;
  }
`;

const fadeInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const Loading: React.FC<LoadingProps> = ({
  message = "Loading...",
  fullScreen = false,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: fullScreen ? "100vh" : "100%",
        width: "100%",
        position: fullScreen ? "fixed" : "absolute",
        top: 0,
        left: 0,
        backgroundColor: fullScreen 
          ? "rgba(248, 250, 252, 0.95)" 
          : "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(8px)",
        zIndex: 1000,
      }}
    >
      {/* Simple Loading Container */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          animation: `${fadeInUp} 0.4s ease-out`,
        }}
      >
        {/* Simple Icon */}
        <DirectionsBus
          sx={{
            fontSize: 32,
            color: theme.palette.primary.main,
            mb: 2,
            animation: `${pulse} 2s ease-in-out infinite`,
          }}
        />

        {/* Simple Loading Dots */}
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            mb: 2,
            alignItems: "center",
          }}
        >
          {[0, 1, 2].map((index) => (
            <Box
              key={index}
              sx={{
                width: 6,
                height: 6,
                backgroundColor: theme.palette.primary.main,
                borderRadius: "50%",
                animation: `${pulse} 1.4s ease-in-out infinite`,
                animationDelay: `${index * 0.16}s`,
              }}
            />
          ))}
        </Box>

        {/* Loading Message */}
        {message && (
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 500,
              textAlign: "center",
              fontSize: "0.95rem",
            }}
          >
            {message}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
